import { Plus } from "lucide-react";
import COBuilder from "@/components/COBuilder";
import { btn } from "@/components/common/btn";

// Change orders tab on the WO form (UI sweep batch 4: the tab carries the
// name, so no section title / toggle). Toolbar: green "+ Change order" on
// the left, counts + approved subtotal far right; then the active COs and
// a separated declined / not-approved list. State stays in the parent —
// this component only receives the resolved data and imperative handlers.
export function WOChangeOrdersSection({
  editingWO,
  form,
  coSubtotal, hasApprovedCOs,
  fmt,
  addChangeOrder,
  updateChangeOrder,
  removeChangeOrder,
  handleSaveCO,
  handleSubmitCO,
  handleRevertCO,
  handleApproveCO,
  handleUnapproveCO,
  handleNotApproveCO,
  handleUnlockedCO,
  masterScopes,
  masterPartsList,
  clientName,
  siteName,
  siteAddress,
}) {
  if (!editingWO) return null;

  const allCOs = form.change_orders || [];
  const activeCOs = allCOs.filter(co => co.status !== "declined" && co.status !== "not_approved");
  const declinedCOs = allCOs.filter(co => co.status === "declined" || co.status === "not_approved");
  const pendingCount = allCOs.filter(co => co.status === "draft" || co.status === "submitted").length;

  const shared = (co) => ({
    co,
    onChange: updateChangeOrder,
    onRemove: removeChangeOrder,
    onSubmit: handleSubmitCO,
    onRevert: handleRevertCO,
    onApprove: handleApproveCO,
    onUnapprove: handleUnapproveCO,
    onNotApprove: handleNotApproveCO,
    onUnlocked: handleUnlockedCO,
    masterScopes,
    masterParts: masterPartsList,
    isEditable: true,
    woId: editingWO.id,
    woNumber: editingWO.wo_number || form.wo_number,
    woTitle: editingWO.title || form.title,
    woSiteId: form.site_id,
    clientName,
    siteName,
    siteAddress,
    taxRate: form.tax_rate,
    taxAuthority: form.tax_authority,
    hideTax: !!form.hide_tax,
  });

  return (
    <div className="space-y-3" data-testid="change-orders-section">
      <div className="flex flex-wrap items-center gap-2" data-testid="change-orders-toolbar">
        <button type="button" className={btn("write")} onClick={addChangeOrder} data-testid="btn-add-change-order">
          <Plus className="w-3 h-3" /> CO
        </button>
        <div className="ml-auto flex items-center gap-3 text-xs text-slate-500">
          <span className="num">{allCOs.length} CO{allCOs.length !== 1 ? "s" : ""}</span>
          {hasApprovedCOs && <span className="text-emerald-600 num">+{fmt(coSubtotal)}</span>}
          {pendingCount > 0 && <span className="text-amber-600 num">{pendingCount} pending</span>}
        </div>
      </div>

      {activeCOs.length === 0 ? (
        <div className="text-center py-6 text-slate-400 text-sm border rounded-lg border-dashed">
          No change orders yet. Click &quot;+ CO&quot; to start.
        </div>
      ) : (
        activeCOs.map(co => {
          // Item 11 — Contract summary props. Original contract value =
          // the WO's signed/proposed total; prior approved COs = approved
          // totals that came before THIS CO, so Revised compounds per CO.
          const thisIdx = allCOs.findIndex(x => x.id === co.id);
          const priorApprovedTotal = allCOs
            .slice(0, thisIdx >= 0 ? thisIdx : 0)
            .filter(c => c.status === "approved")
            .reduce((s, c) => s + (parseFloat(c.total) || 0), 0);
          const originalContractTotal = parseFloat(editingWO?.total) || 0;
          return (
            <COBuilder
              key={co.id}
              {...shared(co)}
              onSave={handleSaveCO}
              originalContractTotal={originalContractTotal}
              priorApprovedTotal={priorApprovedTotal}
            />
          );
        })
      )}

      {declinedCOs.length > 0 && (
        <div className="pt-2 border-t border-dashed border-slate-200 space-y-2">
          <h5 className="text-xs text-slate-400 font-medium">Declined / not approved</h5>
          {declinedCOs.map(co => <COBuilder key={co.id} {...shared(co)} />)}
        </div>
      )}
    </div>
  );
}
